/**
 * useTextToSpeech — Speak text aloud via backend TTS (shared across go-pwa, web-client)
 *
 * Flow:
 *   1. speak(text) strips markdown, detects language
 *   2. Calls ttsFn(text, lang) → {audio?, mimeType?, text?, useBrowserTTS?}
 *   3. Plays returned base64 audio blob via Web Audio API
 *   4. Falls back to SpeechSynthesis API if no ttsFn or no audio returned
 */

import { useState, useRef, useCallback } from 'react';
import type { VoiceRole } from './voice-types';
import type { VoiceTtsContext } from './tts-types';

export type TtsFn = (
  text: string,
  lang: string,
  context?: VoiceTtsContext,
) => Promise<{
  audio?: string;
  mimeType?: string;
  text?: string;
  useBrowserTTS?: boolean;
}>;

export interface UseTextToSpeechOpts {
  /** Override language (BCP-47). Auto-detected from text if omitted. */
  lang?: string;
  role?: VoiceRole;
  surface?: string;
  profile?: string;
  voiceStyle?: string;
  allowBrowserFallback?: boolean;
  onStart?: () => void;
  onEnd?: () => void;
  onError?: (msg: string) => void;
  /**
   * Backend TTS function. If omitted, falls back to browser SpeechSynthesis.
   * Signature: (text, lang) => Promise<{audio?, mimeType?, text?, useBrowserTTS?}>
   */
  ttsFn?: TtsFn;
}

/** Detect language from text content */
function detectLang(text: string): string {
  const cjk = text.match(/[\u4e00-\u9fff\u3400-\u4dbf]/g);
  const cjkRatio = (cjk?.length ?? 0) / Math.max(text.replace(/\s/g, '').length, 1);
  if (cjkRatio > 0.3) return 'zh-CN';
  const jp = text.match(/[\u3040-\u309f\u30a0-\u30ff]/g);
  if ((jp?.length ?? 0) > 5) return 'ja';
  const kr = text.match(/[\uac00-\ud7af]/g);
  if ((kr?.length ?? 0) > 5) return 'ko';
  return 'en-US';
}

/** Strip markdown for cleaner speech */
function stripMarkdown(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, ' code block ')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/#{1,6}\s/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[|`~>]/g, ' ')
    .trim();
}

function base64ToBlob(base64: string, mimeType: string): Blob {
  const bytes = atob(base64);
  const buffer = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) buffer[i] = bytes.charCodeAt(i);
  return new Blob([buffer], { type: mimeType });
}

export function useTextToSpeech(opts: UseTextToSpeechOpts = {}) {
  const [speaking, setSpeaking] = useState(false);
  const [loading, setLoading] = useState(false);
  const [speakingId, setSpeakingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      audioRef.current = null;
    }
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    setSpeaking(false);
    setLoading(false);
    setSpeakingId(null);
  }, []);

  const speak = useCallback(async (text: string, messageId?: string) => {
    stop();
    if (!text.trim()) return;

    const cleanText = stripMarkdown(text);
    if (!cleanText) return;

    setLoading(true);
    setSpeakingId(messageId ?? null);

    const lang = opts.lang ?? detectLang(cleanText);
    const context: VoiceTtsContext = {
      role: opts.role,
      surface: opts.surface,
      profile: opts.profile,
      voiceStyle: opts.voiceStyle,
      requireRemoteAudio: opts.allowBrowserFallback === false,
    };

    try {
      if (opts.ttsFn) {
        const res = await opts.ttsFn(cleanText.slice(0, 4000), lang, context);

        if (res.audio) {
          const audioBlob = base64ToBlob(res.audio, res.mimeType ?? 'audio/mp3');
          const url = URL.createObjectURL(audioBlob);
          const audio = new Audio(url);
          audioRef.current = audio;

          audio.onplay = () => {
            setLoading(false);
            setSpeaking(true);
            opts.onStart?.();
          };
          audio.onended = () => {
            setSpeaking(false);
            setSpeakingId(null);
            URL.revokeObjectURL(url);
            opts.onEnd?.();
          };
          audio.onerror = () => {
            setSpeaking(false); setLoading(false); setSpeakingId(null);
            URL.revokeObjectURL(url);
            opts.onError?.('Audio playback failed');
          };
          await audio.play();
          return;
        }

        // Backend returned text or useBrowserTTS flag
        if (res.useBrowserTTS || res.text) {
          if (opts.allowBrowserFallback === false) {
            setLoading(false);
            setSpeakingId(null);
            opts.onError?.('High-quality voice unavailable');
            return;
          }
          speechSynthesisFallback(
            res.text ?? cleanText,
            lang,
            setSpeaking,
            setLoading,
            setSpeakingId,
            opts.onError,
            opts.onStart,
            opts.onEnd,
          );
          return;
        }
      }

      // No ttsFn provided — use browser TTS directly
      if (opts.allowBrowserFallback === false) {
        setLoading(false);
        setSpeakingId(null);
        opts.onError?.('High-quality voice unavailable');
        return;
      }
      speechSynthesisFallback(
        cleanText,
        lang,
        setSpeaking,
        setLoading,
        setSpeakingId,
        opts.onError,
        opts.onStart,
        opts.onEnd,
      );
    } catch (err) {
      setLoading(false);
      setSpeakingId(null);
      opts.onError?.(err instanceof Error ? err.message : 'TTS failed');
    }
  }, [opts.allowBrowserFallback, opts.lang, opts.onEnd, opts.onError, opts.onStart, opts.profile, opts.role, opts.surface, opts.ttsFn, opts.voiceStyle, stop]);

  return { speak, stop, speaking, loading, speakingId };
}

function speechSynthesisFallback(
  text: string,
  lang: string,
  setSpeaking: (v: boolean) => void,
  setLoading: (v: boolean) => void,
  setSpeakingId: (v: string | null) => void,
  onError?: (msg: string) => void,
  onStart?: () => void,
  onEnd?: () => void,
) {
  if (!('speechSynthesis' in window)) {
    setLoading(false);
    setSpeakingId(null);
    onError?.('TTS not available');
    return;
  }
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = lang;
  utterance.onstart = () => {
    setLoading(false);
    setSpeaking(true);
    onStart?.();
  };
  utterance.onend = () => {
    setSpeaking(false);
    setSpeakingId(null);
    onEnd?.();
  };
  utterance.onerror = () => { setSpeaking(false); setLoading(false); setSpeakingId(null); };
  window.speechSynthesis.speak(utterance);
}
